import { CircleCheck } from 'lucide-react-native';
import { useCallback, useEffect } from 'react';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Toaster, ToasterProps } from '@/components/toaster';
import { useToast } from '@/components/ui/toast';
import { supportContactHint } from '@/constants/support';
import { useHaptic } from '@/hooks/useHaptic';
import { showWarningToastAtom, store, userIdAtom } from '@/services/globalState';
import { isApiErrorV2 } from '@/util/fetchClient';

/** Read when the toast shows, since the same toaster serves signed-in and signed-out screens. */
const defaultErrorMessage = () =>
  `Please try again. If this problem persists, please try restarting the Primum app or ${supportContactHint(!!store.get(userIdAtom))}`;

export type ShowToastProps = Omit<ToasterProps, 'action' | 'id' | 'close'> & { duration?: number };

/**
 * Returns stable show* callbacks. The reference stability matters because
 * these are commonly passed into useMemo/useEffect/useCallback dependency
 * arrays across the app (e.g. ChatContent's renderContext memo). Previously
 * each call produced fresh function references on every render, defeating
 * downstream memoization and contributing to the chat re-render storm we
 * see during conversation switches.
 *
 * Stability is achieved without restricting closures over `toast`/`haptic`:
 * useCallback dependencies are explicit and reference the values returned by
 * the underlying hooks. Those hooks already return stable values per provider
 * instance, so the show* callbacks themselves stay stable for the lifetime
 * of this component (subject to provider remounts).
 */
export function useToaster() {
  const toast = useToast();

  const haptic = useHaptic();

  const insets = useSafeAreaInsets();

  /**
   * Top-placed toasts. The gluestack toast layer wraps each toast in React
   * Native's `SafeAreaView`, which is a no-op on Android, so under edge-to-edge
   * (transparent status bar, content drawn behind it) a top toast renders under
   * the status bar / camera cutout. Pad the top inset ourselves on Android; iOS
   * is already handled by that `SafeAreaView`, so adding it there would double.
   */
  const topInset = Platform.OS === 'android' ? insets.top : 0;

  const close = useCallback(
    (id: string) => {
      toast.close(id);
    },
    [toast]
  );

  const show = useCallback(
    (props: Omit<ToasterProps, 'id' | 'close'> & { duration?: number }) => {
      const { duration = 3000, ...toasterProps } = props;

      const id = toast.show({
        placement: 'top',
        duration,
        render: ({ id }) => (
          <View style={{ marginTop: topInset }}>
            <Toaster {...toasterProps} id={id} close={close} />
          </View>
        ),
      });

      return () => {
        toast.close(id);
      };
    },
    [toast, close, topInset]
  );

  const showError = useCallback(
    ({
      message = defaultErrorMessage(),
      apiError,
      ...props
    }: ShowToastProps & {
      apiError?: {
        error: unknown;
        errorPatternMatcher?: Record<string, string>;
      };
    }) => {
      haptic.error();

      if (!apiError?.error || !isApiErrorV2(apiError.error)) {
        return show({ ...props, message, action: 'error' });
      }

      let title = '';

      if (isApiErrorV2(apiError.error)) {
        title = apiError.error.body.error;
      }

      if (apiError.errorPatternMatcher?.[title]) {
        title = apiError.errorPatternMatcher[title];
      }

      return show({
        ...props,
        action: 'error',
        title,
        message: '',
      });
    },
    [haptic, show]
  );

  const showSuccess = useCallback(
    (props: ShowToastProps) => {
      haptic.success();

      return show({ ...props, action: 'success', icon: CircleCheck });
    },
    [haptic, show]
  );

  const showInfo = useCallback(
    (props: ShowToastProps) => show({ ...props, action: 'info' }),
    [show]
  );

  const showWarning = useCallback(
    (props: ShowToastProps) => {
      haptic.warning();

      return show({ ...props, action: 'warning' });
    },
    [haptic, show]
  );

  useEffect(() => {
    store.set(showWarningToastAtom, () => showWarning);
  }, [showWarning]);

  return {
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };
}
